import type { Project } from "@/features/leaderboard/types/index.ts";
import { useRunwayStressList } from "../hooks/use-intelligence-queries.ts";
import { ProjectNameLink } from "./ProjectNameLink.tsx";

function formatDollars(n: number | null): string {
  if (n == null) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function formatMonths(days: number): string {
  const months = Math.round(days / 30);
  if (months >= 24) return `${Math.floor(months / 12)}y ${months % 12}mo`;
  return `${months}mo`;
}

// Stage labels from funding_rounds that aren't real stages — render
// as em-dash instead of leaking "Unknown" / "--" to BD users.
const NOISE_STAGES = new Set(["unknown", "--", "-", ""]);

function formatStage(stage: string | null): string {
  if (!stage) return "—";
  if (NOISE_STAGES.has(stage.toLowerCase().trim())) return "—";
  return stage;
}

interface RunwayStressListProps {
  onOpenProjectDetail?: (project: Project) => void;
}

export function RunwayStressList({ onOpenProjectDetail }: RunwayStressListProps = {}) {
  const { data, isLoading, isError } = useRunwayStressList(50);

  if (isLoading && !data) return <div className="intel-loading">Loading runway stress list…</div>;
  // Only flip to error banner when we have nothing to show — don't
  // clobber a cached table during a transient background refetch.
  if (isError && !data) return <div className="intel-error">Failed to load runway stress list</div>;
  if (!data) return null;

  return (
    <section className="intel-section">
      <header className="intel-section__header">
        <h3 className="intel-section__title">Runway stress list</h3>
        <p className="intel-section__subtitle">
          Projects whose most recent funding round is over 12 months old, alpha score ≥20, and score
          hasn't dropped more than 5 points in the last 30 days. Negotiation-priority leads for BD:
          solid quality holding up while burn continues, likely raising or near TGE. Sorted by
          oldest round first.
        </p>
      </header>

      {data.length === 0 ? (
        <div className="intel-coinv-empty">No projects match the runway stress criteria.</div>
      ) : (
        <div className="intel-runway-wrap">
          <table className="intel-runway">
            <thead>
              <tr>
                <th>Project</th>
                <th>Family</th>
                <th>Last round</th>
                <th className="intel-runway__num-head">Months ago</th>
                <th className="intel-runway__num-head">Raised</th>
                <th className="intel-runway__num-head">Score</th>
                <th className="intel-runway__num-head">Δ 30d</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.projectId}>
                  <td className="intel-runway__project">
                    <ProjectNameLink
                      projectId={row.projectId}
                      label={row.name ?? row.twitterHandle}
                      onOpenProjectDetail={onOpenProjectDetail}
                    />
                  </td>
                  <td className="intel-runway__family">{row.family ?? "Other"}</td>
                  <td className="intel-runway__stage">{formatStage(row.latestRoundStage)}</td>
                  <td className="intel-runway__num">{formatMonths(row.daysSinceLatestRound)}</td>
                  <td className="intel-runway__num">{formatDollars(row.fundingRaisedUsd)}</td>
                  <td className="intel-runway__num">{row.alphaScore.toFixed(1)}</td>
                  <td
                    className={`intel-runway__num intel-runway__delta ${
                      row.scoreDelta30d == null
                        ? "intel-runway__delta--none"
                        : row.scoreDelta30d > 0
                          ? "intel-runway__delta--up"
                          : row.scoreDelta30d < 0
                            ? "intel-runway__delta--down"
                            : "intel-runway__delta--flat"
                    }`}
                  >
                    {row.scoreDelta30d == null
                      ? "—"
                      : row.scoreDelta30d > 0
                        ? `+${row.scoreDelta30d.toFixed(1)}`
                        : row.scoreDelta30d.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
